/**
 * The slot queue engine (DS-1).
 *
 * "Fill several targets without reopening the dialog." One picker open, N
 * targets, a cursor that advances to the next EMPTY one and wraps.
 *
 * PROMOTED, NOT COPIED. This is `createQueue` lifted verbatim out of
 * `editing/image-picker/session.js`, where it worked and was trapped in one
 * surface. Two things are parameterised and nothing else is: the noun in a
 * generated slot label (`Image 1` → `${noun} 1`) and the noun in the remaining
 * sentence (`1 image left.`). Everything else was already right.
 *
 * NO DOM. This module decides; `collection-chrome.js`'s `queue` option paints.
 * Keeping the split is what let the image picker keep its behaviour byte-for-
 * byte while its chrome moved house — and it is why this file is unit-testable
 * without a jsdom document.
 *
 * THE DEGRADATION HINGE is `isDrained()`. With no slots, `entries` is empty,
 * `current()` is null — so a caller sees no `slot` key at all, exactly as
 * before — and `isDrained()` is true the moment anything is filled. That is
 * what makes a queue-less open byte-identical to a dialog that never heard of
 * queues, and it is the reason a consumer can adopt the option without
 * branching on whether it has slots.
 */

/**
 * @typedef {object} SlotInput
 * @property {string|number} [id]
 * @property {string} [label]
 * @property {string} [ratio]
 * @property {string} [thumb]
 * @property {boolean} [filled]
 * @property {object} [data] — the consumer's own payload, carried untouched.
 */

/**
 * @param {SlotInput[]|null|undefined} input
 * @param {{noun?: string, nounPlural?: string}} [opts]
 *   `noun` names a slot whose caller supplied no label (`Image 3`, `Shape 3`).
 *   `nounPlural` is used by `remainingLabel()`; it defaults to `noun + 's'`,
 *   which is right for both of this initiative's consumers and wrong for a
 *   future irregular one — hence the explicit parameter.
 */
export function createSlotQueue(input, { noun = 'Image', nounPlural } = {}) {
  const plural = nounPlural || `${noun}s`;
  const entries = Array.isArray(input)
    ? input.map((s, i) => ({
        id: String(s?.id ?? i),
        label: String(s?.label || `${noun} ${i + 1}`),
        ratio: String(s?.ratio || ''),
        thumb: String(s?.thumb || ''),
        filled: !!s?.filled,
        data: s?.data || {}
      }))
    : [];
  let cursor = 0;
  let anyFilled = false;
  return {
    entries,
    get size() {
      return entries.length;
    },
    get cursor() {
      return cursor;
    },
    current: () => entries[cursor] || null,
    select(index) {
      if (index < 0 || index >= entries.length) return false;
      cursor = index;
      return true;
    },
    markFilled(slot, thumbUrl) {
      anyFilled = true;
      if (!slot) return;
      slot.filled = true;
      if (thumbUrl) slot.thumb = String(thumbUrl);
    },
    filledCount: () => entries.filter((s) => s.filled).length,
    isDrained: () => (entries.length === 0 ? anyFilled : entries.every((s) => s.filled)),
    /**
     * The next EMPTY slot after the cursor, WRAPPING — so re-filling a slot in
     * the middle of a run sends you back to the gap rather than to the end.
     * Staying put when nothing is empty is unreachable from a completed pick
     * (drained settles first) and is the safe answer for any other caller.
     */
    advance() {
      const n = entries.length;
      for (let i = 1; i <= n; i += 1) {
        const idx = (cursor + i) % n;
        if (!entries[idx].filled) {
          cursor = idx;
          return;
        }
      }
    },
    remainingLabel() {
      const left = entries.filter((s) => !s.filled).length;
      return left === 1 ? `1 ${noun.toLowerCase()} left.` : `${left} ${plural.toLowerCase()} left.`;
    }
  };
}
